import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation } from 'react-router-dom';
import companyLogoIconPath from '../../../../assets/code-cube-logo.svg';
import companyLogoNamePath from '../../../../assets/code-cube-name.svg';
import "./styles.scss";

import { AuthContext } from '../../../../services/auth.service';

import MenuCategory from "./MenuCategory";
import ProfileArea from "./ProfileArea";
import Dropdown from '../../../elements/Dropdown';

const Sidebar = () => {
    const { companyInfo, companyList } = useContext(AuthContext);
    const { adminUser } = useContext(AuthContext);
    const [tagMonitorActive, setTagMonitorActive] = useState(false);
    const [tagMonitorServer, setTagMonitorServer] = useState(false);
    const [dataLayerGuardActive, setDataLayerGuardActive] = useState(false);
    const [sitespeedMonitorActive, setSitespeedMonitorActive] = useState(false);
    const [expandedCategory, setExpandedCategory] = useState(null);
    const location = useLocation();

    useEffect(() => {
        setTagMonitorActive(companyInfo?.tag_monitor);
        setDataLayerGuardActive(companyInfo?.datalayer_guard);
        setSitespeedMonitorActive(companyInfo?.sitespeed_monitor);
    }, [companyInfo]);

    // Function to check if any link in a category is active
    const isCategoryActive = (links) => {
        if (!links || links.length === 0) return false;
        
        // Special case for homepage - don't highlight any category
        if (location.pathname === '/') {
            return false;
        }
        
        return links.some(link => {
            if (link.children) {
                return location.pathname === link.to || 
                       link.children.some(child => {
                           const childPath = child.to.split('?')[0];
                           return location.pathname === childPath;
                       });
            }
            return location.pathname === link.to;
        });
    };

    const dashboardLinks = [
        { 
            name: "DataLayer Guard", 
            to: "/datalayer_guard_dashboard", 
            active: dataLayerGuardActive,
            show: dataLayerGuardActive 
        },
        {
            name: 'Tag Monitor',
            to: '/tag_monitor_dashboard',
            active: tagMonitorActive,
            show: tagMonitorActive,
            children: [
                {
                    name: 'client-side',
                    to: '/tag_monitor_dashboard?view=client'
                },
                {
                    name: 'server-side',
                    to: '/tag_monitor_dashboard?view=server'
                }
            ]
        }
    ].filter(link => link.show);

    const configLinks = [
        { 
            name: "DataLayer Guard", 
            to: "/datalayer_guard_config", 
            active: dataLayerGuardActive,
            show: dataLayerGuardActive 
        },
        { 
            name: "Sitespeed Monitor", 
            to: "/sitespeed_monitor_config", 
            active: sitespeedMonitorActive,
            show: sitespeedMonitorActive 
        },
        { 
            name: "Tag Monitor", 
            to: "/tag_monitor_config", 
            active: tagMonitorActive,
            show: tagMonitorActive 
        },
    ].filter(link => link.show);

    const accountSettingsLinks = [
        { name: "Account & organization", to: "/account_settings", active: true, show: true },
        { name: "Billing and payments", to: "/", active: false, show: true },
        { name: "Team members", to: "/", active: false, show: true },
    ];

    if (adminUser) {
        accountSettingsLinks.push(
            { name: "Create Datasets", to: "/create_dataset", active: true, show: true }
        )
    }

    const supportLinks = [
        { name: "Documentation", to: "https://docs.code-cube.io/", active: true, show: true },
        { name: "Requests", to: "/support_request", active: true, show: true },
    ];

    const [hovered, handleHovering] = useState(false);
    const [sidebarClass, handleSidebarClass] = useState();
    const hoverSidebar = () => handleHovering(true);
    const hoverOutSidebar = () => handleHovering(false);

    useEffect(() => {
        handleSidebarClass(`sidebar-menu ${hovered ? "hovered" : ""}`);
    }, [hovered]);

    return (
        <div className={sidebarClass} onMouseEnter={hoverSidebar} onMouseLeave={hoverOutSidebar}>
            <div className="sidebar-container">
                <div className="sidebar-container-top mt-5">
                    <div className="sidebar-logo-area">
                        <Link className="sidebar-logo" to="/" onClick={() => hoverOutSidebar()}>
                            <div className="sidebar-logo-container">
                                <img className="sidebar-logo-icon" src={companyLogoIconPath} alt="Code Cube Logo" height="26" />
                                <img className="sidebar-logo-name title-sliding" src={companyLogoNamePath} alt="Code Cube" height="16" />
                            </div>
                        </Link>
                    </div>
                    <div className="sidebar-elems-area links-area">
                        {dashboardLinks.length > 0 && (
                            <MenuCategory
                                categoryIcon={
                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <line x1="4" y1="14" x2="4" y2="11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                        <line x1="9" y1="14" x2="9" y2="8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                        <line x1="14" y1="14" x2="14" y2="5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                    </svg>
                                }
                                categoryName={<span style={{ fontSize: "14px" }}>Dashboards</span>}
                                links={dashboardLinks}
                                hovered={hovered}
                                hoverOutSidebar={hoverOutSidebar}
                                defaultCollapsed={!isCategoryActive(dashboardLinks)}
                                isActive={isCategoryActive(dashboardLinks)}
                                expandedCategory={expandedCategory}
                                setExpandedCategory={setExpandedCategory}
                                categoryId="dashboards"
                            />
                        )}
                        {configLinks.length > 0 && (
                            <MenuCategory
                                categoryIcon={
                                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <line x1="3" y1="4" x2="3" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                        <line x1="9" y1="4" x2="9" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                        <line x1="15" y1="4" x2="15" y2="14" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                        <circle cx="3" cy="7" r="1.5" fill="currentColor"/>
                                        <circle cx="9" cy="11" r="1.5" fill="currentColor"/>
                                        <circle cx="15" cy="7" r="1.5" fill="currentColor"/>
                                    </svg>
                                }
                                categoryName={<span style={{ fontSize: "14px" }}>Configurations</span>}
                                links={configLinks}
                                hovered={hovered}
                                hoverOutSidebar={hoverOutSidebar}
                                defaultCollapsed={!isCategoryActive(configLinks)}
                                isActive={isCategoryActive(configLinks)}
                                expandedCategory={expandedCategory}
                                setExpandedCategory={setExpandedCategory}
                                categoryId="configurations"
                            />
                        )}
                        <MenuCategory
                            categoryIcon={
                            <svg width="22" height="15" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <g clipPath="url(#clip0_21_211)">
                                <path d="M9.00001 8.27896C9.48531 8.52275 9.90138 8.91398 10.2051 9.40321C10.2652 9.50009 10.2953 9.54853 10.3057 9.61561C10.3268 9.75192 10.2336 9.91951 10.1067 9.97346C10.0442 10 9.9739 10 9.83334 10M8.33334 6.84408C8.82725 6.59863 9.16667 6.08895 9.16667 5.5C9.16667 4.91105 8.82725 4.40137 8.33334 4.15592M7.66667 5.5C7.66667 6.32843 6.9951 7 6.16667 7C5.33825 7 4.66667 6.32843 4.66667 5.5C4.66667 4.67157 5.33825 4 6.16667 4C6.9951 4 7.66667 4.67157 7.66667 5.5ZM3.85308 9.31278C4.38452 8.51485 5.22313 8 6.16667 8C7.11022 8 7.94883 8.51485 8.48026 9.31278C8.59669 9.48759 8.6549 9.57499 8.6482 9.68664C8.64298 9.77357 8.58599 9.88 8.51653 9.93253C8.42731 10 8.30462 10 8.05922 10H4.27413C4.02873 10 3.90603 10 3.81682 9.93253C3.74736 9.88 3.69037 9.77357 3.68515 9.68664C3.67845 9.57499 3.73666 9.48759 3.85308 9.31278Z" stroke="currentColor" strokeWidth="0.582142" strokeLinecap="round" strokeLinejoin="round"/>
                                </g>
                                <path d="M0.647888 10.1603V1.35609C0.647888 1.15943 0.807314 1 1.00398 1H8.14345C8.34011 1 8.49954 1.15943 8.49954 1.35609V2.57228" stroke="currentColor" strokeWidth="0.982222" strokeLinecap="round"/>
                                <rect x="1.95648" y="4.92582" width="0.872405" height="1.30861" rx="0.133533" fill="currentColor"/>
                                <path d="M1.95648 7.67657C1.95648 7.60282 2.01627 7.54304 2.09002 7.54304H2.69535C2.7691 7.54304 2.82889 7.60282 2.82889 7.67657V8.71811C2.82889 8.79186 2.7691 8.85165 2.69535 8.85165H2.09001C2.01627 8.85165 1.95648 8.79186 1.95648 8.71811V7.67657Z" fill="currentColor"/>
                                <rect x="1.95648" y="2.30861" width="0.872405" height="1.30861" rx="0.133533" fill="currentColor"/>
                                <rect x="4.1377" y="2.30867" width="0.872405" height="1.30861" rx="0.133533" fill="currentColor"/>
                                <defs>
                                <clipPath id="clip0_21_211">
                                <rect width="8" height="8" fill="currentColor" transform="translate(3 3)"/>
                                </clipPath>
                                </defs>
                            </svg>
                            }   
                            categoryName={<span style={{ fontSize: "14px" }}>Account settings</span>}
                            links={accountSettingsLinks}
                            hovered={hovered}
                            hoverOutSidebar={hoverOutSidebar}
                            defaultCollapsed={!isCategoryActive(accountSettingsLinks)}
                            isActive={isCategoryActive(accountSettingsLinks)}
                            expandedCategory={expandedCategory}
                            setExpandedCategory={setExpandedCategory}
                            categoryId="account-settings"
                        />
                        <MenuCategory
                            categoryIcon={
                                <svg width="18" height="18" viewBox="0 0 23 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M11.108 8.00224C11.2842 7.50136 11.632 7.079 12.0898 6.80998C12.5475 6.54095 13.0857 6.4426 13.609 6.53237C14.1324 6.62213 14.607 6.89421 14.949 7.30041C15.2909 7.70661 15.4781 8.22072 15.4773 8.75168C15.4773 10.2506 13.2289 11 13.2289 11M13.2579 14H13.2679M13.108 19C17.8024 19 21.608 15.1944 21.608 10.5C21.608 5.80558 17.8024 2 13.108 2C8.4136 2 4.60802 5.80558 4.60802 10.5C4.60802 11.45 4.76387 12.3636 5.05139 13.2166C5.15959 13.5376 5.21369 13.6981 5.22345 13.8214C5.23309 13.9432 5.2258 14.0286 5.19568 14.1469C5.16517 14.2668 5.09782 14.3915 4.96312 14.6408L3.32746 17.6684C3.09415 18.1002 2.97749 18.3161 3.0036 18.4828C3.02634 18.6279 3.11177 18.7557 3.2372 18.8322C3.3812 18.9201 3.62532 18.8948 4.11356 18.8444L9.23459 18.315C9.38967 18.299 9.46721 18.291 9.53788 18.2937C9.6074 18.2963 9.65647 18.3029 9.72426 18.3185C9.79318 18.3344 9.87984 18.3678 10.0532 18.4345C11.0012 18.7998 12.0312 19 13.108 19Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            }
                            categoryName={<span style={{ fontSize: "0.9rem" }}>Support</span>}
                            links={supportLinks}
                            hovered={hovered}
                            hoverOutSidebar={hoverOutSidebar}
                            expandedCategory={expandedCategory}
                            setExpandedCategory={setExpandedCategory}
                            categoryId="support"
                        />
                    </div>
                </div>
                <div className="sidebar-container-bottom">
                    <div className="sidebar-elems-area">
                        {companyList?.length > 1 ?
                            <Dropdown
                                list={companyList}
                                hovered={hovered}
                                hoverOutSidebar={hoverOutSidebar}
                            />
                            :
                            <></>
                        }

                        <ProfileArea
                            hovered={hovered}
                            size={18}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Sidebar;